/**
 * KEVIA — Preachings Query Adapter
 * Read-only. Returns upcoming preaching assignments for the district.
 */

import { supabase } from "../../supabase";

const MAX_ROWS = 8;

/**
 * Upcoming preaching assignments for the next N days.
 * Queries via the active published roster for this district.
 *
 * @param {import('../permissionFilters').PermissionScope} scope
 * @param {number} [daysAhead=14]
 * @returns {Promise<Object[]>}
 */
export async function getUpcomingPreachings(scope, daysAhead = 14) {
  if (!scope.district_id) return [];

  const today  = new Date().toISOString().slice(0, 10);
  const future = new Date(Date.now() + daysAhead * 86_400_000).toISOString().slice(0, 10);

  // Step 1: find the active published roster for this district
  const { data: rosters, error: rErr } = await supabase
    .from("preaching_rosters")
    .select("id")
    .eq("district_id", scope.district_id)
    .eq("status", "published")
    .order("created_at", { ascending: false })
    .limit(1);

  if (rErr || !rosters?.length) return [];
  const rosterId = rosters[0].id;

  // Step 2: assignments within the date range, with church name via FK join
  const { data, error } = await supabase
    .from("preaching_assignments")
    .select("id, date, church_id, preacher_name, status, churches(name)")
    .eq("roster_id", rosterId)
    .gte("date", today)
    .lte("date", future)
    .order("date", { ascending: true })
    .limit(MAX_ROWS);

  if (error) throw error;

  // Flatten church name — avoids nested object reaching sanitizer
  return (data ?? []).map((r) => ({
    id:            r.id,
    date:          r.date,
    church_name:   r.churches?.name ?? null,
    preacher_name: r.preacher_name  ?? null,
    status:        r.status,
  }));
}
